import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  ListItemText,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { compact, filter, find, includes, isEmpty, map, size, uniqBy } from 'es-toolkit/compat';
import React from 'react';

import { PanelHeaderSummary } from 'components/StudyDashboard/MultiplexPanels/PanelHeaderSummary';
import { MultiplexPanelView } from 'interfaces/multiplexPanels';
import { Study } from 'interfaces/study';
import {
  getPanelRunIds,
  getPreferenceAvailability,
  ResolvedPanel,
  ResolvedSlide,
  ResultsChoice,
  slideMatchesSearch,
} from 'services/export/workflowResultsSelection';
import { WorkflowExportKind } from 'services/export/workflowExportSource';
import { useStainTypeOptions } from 'utils/queryHooks/uiConstantsHooks';
import Loader from 'components/Loader';
import { ExportErrorAlert } from 'components/ResultsExport/ExportErrorAlert';
import { ExportScopeValue } from 'components/ResultsExport/ExportFormFields';
import { buildExportErrorReport, describeExportFailure } from 'services/export/exportErrorReport';
import { useCurrentLabId } from 'utils/useCurrentLab';
import { WorkflowResultsSelectionController } from './useWorkflowResultsSelection';
import { WorkflowResultsSourceSelect } from './WorkflowResultsSourceSelect';
import { SlideRowResetButton, WorkflowResultsSlideRow } from './WorkflowResultsSlideRow';

const SLIDES_PER_INCREMENT = 50;
// A large study starts with its panels collapsed, so the dialog opens on the panels rather than on 50 slides.
const COLLAPSE_ABOVE_SLIDE_COUNT = 50;

const SAME_AS_ALL_SLIDES = '';
const SELECT_ALL_PANEL_RUNS = '__select-all-panel-runs__';
const CLEAR_PANEL_RUNS = '__clear-panel-runs__';

const describeIgnoredPanelRuns = (count: number) =>
  count === 1
    ? '1 run for this panel is not available and was ignored.'
    : `${count} runs for this panel are not available and were ignored.`;

const describePanelRunSelection = (runIds: string[], getRunLabel: (runId: string) => string) => {
  if (isEmpty(runIds)) return 'No runs selected for this panel';
  if (runIds.length === 1) return getRunLabel(runIds[0]);
  return `${runIds.length} runs selected for this panel`;
};

const panelChoiceOptions: { value: ResultsChoice['source']; label: string }[] = [
  { value: 'published', label: 'Published' },
  { value: 'approved', label: 'Approved (not yet published)' },
  { value: 'latest', label: 'Latest run' },
  { value: 'run', label: 'A specific run' },
];

const countLabel = (count: number, noun: string) => `${count} ${noun}${count === 1 ? '' : 's'}`;

interface WorkflowResultsSelectionProps {
  study: Study;
  kind: WorkflowExportKind;
  controller: WorkflowResultsSelectionController;
  // What the export is, in the words of the Cell typing & marker positivity step.
  description: string;
  // Names the export in the support details of a failure, for example `Export features`.
  operation: string;
  // Alerts and loaders that belong above the panels.
  children?: React.ReactNode;
  // Content under a slide's row, for example its cell tables.
  renderSlideDetail?: (resolved: ResolvedSlide) => React.ReactNode;
  // Content under a panel's heading, for example its column summary.
  renderPanelNote?: (panel: ResolvedPanel) => React.ReactNode;
  // Text matched by the search next to the slide, case, panel and run names.
  getSlideSearchText?: (resolved: ResolvedSlide) => string;
}

// The panel → slides selection shared by the Workflow export dialogs. The panels, slides and runs are those of the
// Cell typing & marker positivity step; each slide exports the results chosen for it, in the order of precedence
// Results for all slides, then Results for this panel, then Results for this slide.
export const WorkflowResultsSelection: React.FC<WorkflowResultsSelectionProps> = ({
  study,
  kind,
  controller,
  description,
  operation,
  children,
  renderSlideDetail,
  renderPanelNote,
  getSlideSearchText,
}) => {
  const { source, selection, model, panelsInput, getRunLabel } = controller;
  const { canViewUnpublishedResults } = source;
  // Orchestrations can arrive before job names. Withhold name-dependent choices until the names resolve.
  const runNamesLoading = canViewUnpublishedResults && source.isLoadingRunOptions && !source.isLoadingRuns;
  const { stainTypeOptions } = useStainTypeOptions();
  const { labId } = useCurrentLabId();
  const [search, setSearch] = React.useState('');
  const [expanded, setExpanded] = React.useState<Record<string, boolean | undefined>>({});
  const [shown, setShown] = React.useState<Record<string, number | undefined>>({});

  const uniqueSlides = uniqBy(
    selection.panels.flatMap((panel) => panel.slides),
    (resolved) => resolved.slide.slideId
  );
  const startsCollapsed = size(uniqueSlides) > COLLAPSE_ABOVE_SLIDE_COUNT;
  const isExpanded = (panelKey: string) => expanded[panelKey] ?? !startsCollapsed;
  const availability = getPreferenceAvailability(model);
  const hasPanelChoice = canViewUnpublishedResults && availability.runs;
  const panelViews = panelsInput.panels?.panels ?? [];
  const hasDefinedPanels = !isEmpty(panelViews);

  const getMarkerNames = (panelKey: string) => {
    const view: MultiplexPanelView | undefined = find(panelViews, { panelId: panelKey });
    return compact(
      map(
        filter(view?.channels, (channel) => channel.saved?.channelModality !== 'dna'),
        ({ saved }) =>
          saved?.displayName?.trim() ||
          (saved?.channelModality !== 'rna' ? find(stainTypeOptions, { id: saved?.stainTypeId })?.displayName : '') ||
          (saved?.channelModality === 'rna' ? 'Unnamed RNA probe' : saved ? 'Unnamed marker' : '')
      )
    );
  };

  const slideMatches = (resolved: ResolvedSlide, panelName: string) =>
    slideMatchesSearch(resolved, panelName, search, getRunLabel) ||
    Boolean(search.trim() && getSlideSearchText?.(resolved).toLowerCase().includes(search.trim().toLowerCase()));

  const withoutResult = size(uniqueSlides) - selection.selectableSlideCount;
  const toggleSlide = (slideId: string, isSelected: boolean) => controller.setSelected([slideId], isSelected);

  const panelResultsSelect = (resolvedPanel: ResolvedPanel) => {
    const { panel, panelChoice } = resolvedPanel;
    const runsOnly = !availability.published && !availability.approved;
    const panelHas = (flag: 'published' | 'approved') =>
      panel.slides.some((slide) =>
        slide.results.some((result) => (flag === 'published' ? result.isPublished : result.isInternallyApproved))
      );
    return (
      <TextField
        select
        size="small"
        label="Results for this panel"
        value={panelChoice?.source ?? SAME_AS_ALL_SLIDES}
        onChange={(event) => {
          const next = event.target.value;
          if (next === SAME_AS_ALL_SLIDES) return controller.choosePanel(panel.key, undefined);
          return controller.choosePanel(
            panel.key,
            next === 'run' ? { source: 'run', runIds: [] } : ({ source: next } as ResultsChoice)
          );
        }}
        sx={{ minWidth: 220 }}
      >
        <MenuItem value={SAME_AS_ALL_SLIDES}>Same as all slides</MenuItem>
        {map(
          filter(panelChoiceOptions, ({ value }) => !(runsOnly && (value === 'published' || value === 'approved'))),
          ({ value, label }) => (
            <MenuItem
              key={value}
              value={value}
              disabled={(value === 'published' || value === 'approved') && !panelHas(value)}
            >
              {label}
            </MenuItem>
          )
        )}
      </TextField>
    );
  };

  const panelRunMenu = (resolvedPanel: ResolvedPanel) => {
    const { panel, showsRunSelector, effectiveRunIds, ignoredRunCount } = resolvedPanel;
    if (!canViewUnpublishedResults || !showsRunSelector || runNamesLoading) return null;
    if (source.isLoadingRunOptions) {
      return <Loader inline message="Loading post-processing run names…" />;
    }
    const runIds = getPanelRunIds(model, panel.key);
    return (
      <Stack spacing={0.5} sx={{ alignSelf: 'flex-start', maxWidth: 420 }}>
        <Select
          size="small"
          multiple
          displayEmpty
          value={effectiveRunIds}
          SelectDisplayProps={{ 'aria-label': `Runs for ${panel.name}` }}
          renderValue={(chosen) => describePanelRunSelection(chosen, getRunLabel)}
          onChange={(event) => {
            const value = event.target.value;
            const chosen = (typeof value === 'string' ? value.split(',') : value) as string[];
            if (includes(chosen, SELECT_ALL_PANEL_RUNS)) return controller.setPanelRuns(panel.key, runIds);
            if (includes(chosen, CLEAR_PANEL_RUNS)) return controller.setPanelRuns(panel.key, []);
            controller.setPanelRuns(
              panel.key,
              filter(runIds, (runId) => includes(chosen, runId))
            );
          }}
          sx={{ minWidth: 280, maxWidth: 420 }}
        >
          <MenuItem value={SELECT_ALL_PANEL_RUNS} divider disabled={isEmpty(runIds)}>
            Select all runs for this panel
          </MenuItem>
          <MenuItem value={CLEAR_PANEL_RUNS} divider disabled={isEmpty(effectiveRunIds)}>
            Clear
          </MenuItem>
          {map(runIds, (runId) => (
            <MenuItem key={runId} value={runId}>
              <Checkbox edge="start" size="small" checked={includes(effectiveRunIds, runId)} tabIndex={-1} />
              <ListItemText primary={getRunLabel(runId)} />
            </MenuItem>
          ))}
        </Select>
        {ignoredRunCount > 0 && (
          <Typography variant="caption" color="text.secondary">
            {describeIgnoredPanelRuns(ignoredRunCount)}
          </Typography>
        )}
      </Stack>
    );
  };

  const renderPanel = (resolvedPanel: ResolvedPanel) => {
    const { panel, slides, selectableCount, selectedCount } = resolvedPanel;
    const matching = filter(slides, (resolved) => slideMatches(resolved, panel.name));
    if (search.trim() && isEmpty(matching)) return null;
    const runMenu = panelRunMenu(resolvedPanel);
    const limit = shown[panel.key] ?? SLIDES_PER_INCREMENT;
    const selectableIds = map(filter(slides, 'hasResult'), (resolved) => resolved.slide.slideId);
    const allSelected = selectableCount > 0 && selectedCount === selectableCount;
    const headingId = `workflow-panel-${panel.key}`;
    return (
      <Accordion
        key={panel.key}
        disableGutters
        expanded={isExpanded(panel.key)}
        onChange={(_event, value) => setExpanded((previous) => ({ ...previous, [panel.key]: value }))}
        role="group"
        aria-label={panel.name}
      >
        <Stack direction="row" alignItems="center" spacing={1} sx={{ pl: 1, pr: 2 }} useFlexGap flexWrap="wrap">
          <Checkbox
            checked={allSelected}
            indeterminate={selectedCount > 0 && !allSelected}
            disabled={selectableCount === 0}
            onChange={(event) => controller.setSelected(selectableIds, event.target.checked)}
            inputProps={{ 'aria-label': `Select all slides in ${panel.name}` }}
          />
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            aria-label={`${isExpanded(panel.key) ? 'Collapse' : 'Expand'} ${panel.name}`}
            id={headingId}
            sx={{ flex: 1, minWidth: 0 }}
          >
            <PanelHeaderSummary title={panel.name} markerNames={getMarkerNames(panel.key)}>
              <Chip
                size="small"
                label={`${countLabel(
                  slides.length,
                  'slide'
                )} · ${selectedCount} of ${selectableCount} selectable selected`}
              />
            </PanelHeaderSummary>
          </AccordionSummary>
          {hasPanelChoice && <Box sx={{ flexShrink: 0 }}>{panelResultsSelect(resolvedPanel)}</Box>}
        </Stack>
        {runMenu && <Box sx={{ px: 2, pb: 1 }}>{runMenu}</Box>}
        <AccordionDetails sx={{ pt: 0 }}>
          <Stack spacing={0.5}>
            {panel.kind === 'unassigned' && (
              <Typography variant="caption" color="text.secondary">
                These slides are not assigned to a panel. They are listed here so that no result is hidden.
              </Typography>
            )}
            {!runNamesLoading && renderPanelNote?.(resolvedPanel)}
            {map(matching.slice(0, limit), (resolved) => (
              <WorkflowResultsSlideRow
                key={`${panel.key}-${resolved.slide.slideId}`}
                resolved={resolved}
                canChooseResults={canViewUnpublishedResults && availability.runs}
                runNamesLoading={runNamesLoading}
                getRunLabel={getRunLabel}
                onToggle={toggleSlide}
                onChooseResults={controller.chooseSlideResults}
              >
                {!runNamesLoading && renderSlideDetail?.(resolved)}
              </WorkflowResultsSlideRow>
            ))}
            {matching.length > limit && (
              <Button
                size="small"
                color="inherit"
                sx={{ alignSelf: 'flex-start' }}
                onClick={() => setShown((previous) => ({ ...previous, [panel.key]: limit + SLIDES_PER_INCREMENT }))}
              >
                Show more slides ({matching.length - limit} remaining)
              </Button>
            )}
          </Stack>
        </AccordionDetails>
      </Accordion>
    );
  };

  const panelsContent = panelsInput.isLoading ? null : isEmpty(selection.panels) ? null : (
    <Stack spacing={1} role="group" aria-label="Panels and slides">
      {map(selection.panels, renderPanel)}
      {search.trim() &&
        selection.panels.every((resolvedPanel) =>
          isEmpty(filter(resolvedPanel.slides, (resolved) => slideMatches(resolved, resolvedPanel.panel.name)))
        ) && (
          <Typography variant="body2" color="text.secondary">
            No matching slides
          </Typography>
        )}
    </Stack>
  );

  return (
    <Stack spacing={1.5}>
      <Typography variant="body2" color="text.secondary">
        {description} Results are chosen for each slide, grouped by its panel.
      </Typography>
      {canViewUnpublishedResults ? (
        <WorkflowResultsSourceSelect
          kind={kind}
          study={study}
          selection={source.selection}
          onChange={controller.choosePreference}
          runs={source.runs}
          ignoredStudyRunCount={source.ignoredStudyRunIds.length}
          isLoadingRuns={source.isLoadingRuns}
          hasRunsError={source.hasRunsError}
          runsError={source.runsError}
        />
      ) : (
        <ExportScopeValue label="Results for all slides">Published results</ExportScopeValue>
      )}
      {selection.unknownPanelCount > 0 && (
        <Typography variant="body2" role="status">
          {selection.unknownPanelCount === 1
            ? '1 panel in the link is not in this study and was ignored.'
            : `${selection.unknownPanelCount} panels in the link are not in this study and were ignored.`}
        </Typography>
      )}
      {canViewUnpublishedResults && selection.differingSlideCount > 0 && (
        <SlideRowResetButton count={selection.differingSlideCount} onReset={controller.resetAllOverrides} />
      )}
      {panelsInput.isLoading && <Loader inline message="Loading panel information across the cohort…" />}
      {Boolean(panelsInput.error) && (
        <ExportErrorAlert
          title="The panels could not be loaded"
          errors={[describeExportFailure(panelsInput.error, 'The panels could not be loaded.')]}
          report={buildExportErrorReport({
            operation: `${operation} (list panels)`,
            studyId: study.id,
            labId,
            source: 'Multiplex panels',
            errors: [describeExportFailure(panelsInput.error, 'The panels could not be loaded.')],
          })}
        />
      )}
      {!hasDefinedPanels && !panelsInput.isLoading && !panelsInput.error && (
        <Alert severity="info">
          No panels are defined for this study, so all its slides are listed together under All study slides.
        </Alert>
      )}
      {runNamesLoading ? <Loader inline message="Loading post-processing run names…" /> : children}
      {size(uniqueSlides) > 1 && (
        <Stack direction="row" spacing={1} alignItems="center" useFlexGap flexWrap="wrap">
          <TextField
            size="small"
            type="search"
            label="Search slides, cases, panels or runs"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            sx={{ flex: 1, minWidth: 260 }}
          />
          <Button size="small" color="inherit" onClick={controller.selectAllSlides}>
            Select all slides
          </Button>
          <Button size="small" color="inherit" onClick={controller.selectNoSlides}>
            Select none
          </Button>
        </Stack>
      )}
      {!isEmpty(uniqueSlides) && (
        <Typography variant="body2" color="text.secondary" role="status">
          {selection.selectedSlideCount} of {countLabel(selection.selectableSlideCount, 'selectable slide')} selected
          across {countLabel(selection.panels.length, 'panel')}
          {model.resultsInventory === 'unavailable' && withoutResult > 0
            ? '; post-processing runs could not be loaded, so those results are not known'
            : withoutResult > 0
            ? `; ${withoutResult} without a result to export`
            : ''}
          .
        </Typography>
      )}
      {panelsContent}
    </Stack>
  );
};
